
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertCircle, Trash } from "lucide-react";
import { Input } from "@/components/ui/input";

interface DeleteProgramsDialogProps {
  isOpen: boolean;
  isDeleting: boolean;
  selectedCount: number;
  onClose: () => void;
  onDelete: () => Promise<void>;
}

export function DeleteProgramsDialog({
  isOpen,
  isDeleting,
  selectedCount,
  onClose,
  onDelete
}: DeleteProgramsDialogProps) {
  const [confirmText, setConfirmText] = useState("");
  const isConfirmed = confirmText === "Confirm";

  const handleDelete = async () => {
    if (isConfirmed) {
      await onDelete();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="glass backdrop-blur-lg bg-white/10 border-white/20 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <AlertCircle className="h-5 w-5 text-destructive" />
            Confirm Bulk Delete
          </DialogTitle>
          <DialogDescription className="text-white/70">
            Are you sure you want to delete {selectedCount} selected program(s)? 
            This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        
        <div className="mt-2 mb-4">
          <p className="text-sm font-medium mb-2 text-white">
            Type <span className="font-bold">Confirm</span> below to proceed:
          </p>
          <Input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type 'Confirm' to proceed"
            className="bg-white/10 border-white/20 text-white"
          />
        </div>
        
        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting || !isConfirmed}
          >
            {isDeleting ? (
              <>
                <span className="animate-spin mr-2">◌</span>
                Deleting...
              </>
            ) : (
              <>
                <Trash className="mr-2 h-4 w-4" />
                Delete Programs
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
